import { useState, useEffect } from 'react'
import Calendar from 'react-calendar'
import { useTasks } from '../context/TaskContext'
import TaskItem from '../components/TaskItem'
import { format, isSameDay, parseISO } from 'date-fns'
import 'react-calendar/dist/Calendar.css'

function CalendarPage() {
  const { tasks, fetchTasks } = useTasks()
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [tasksForDate, setTasksForDate] = useState([])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  useEffect(() => {
    const filtered = tasks.filter(task => {
      if (!task.dueDate) return false
      const taskDate = parseISO(task.dueDate)
      return isSameDay(taskDate, selectedDate)
    })
    setTasksForDate(filtered)
  }, [selectedDate, tasks])

  // Get dates with tasks for tile content
  const getTileContent = ({ date }) => {
    const dayTasks = tasks.filter(task => {
      if (!task.dueDate) return false
      const taskDate = parseISO(task.dueDate)
      return isSameDay(taskDate, date)
    })

    if (dayTasks.length === 0) return null

    const completedCount = dayTasks.filter(t => t.status === 'completed').length
    const hasHighPriority = dayTasks.some(t => t.priority === 'high' && t.status !== 'completed')

    return (
      <div className="flex flex-col items-center mt-1">
        <div className={`w-2 h-2 rounded-full ${
          completedCount === dayTasks.length 
            ? 'bg-green-500' 
            : hasHighPriority 
              ? 'bg-red-500' 
              : 'bg-blue-500'
        }`} />
        {dayTasks.length > 1 && (
          <span className="text-xs text-gray-500">{dayTasks.length}</span>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
        Calendar
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <div className="card">
            <Calendar
              onChange={setSelectedDate}
              value={selectedDate}
              tileContent={getTileContent}
              className="w-full border-0"
            />
          </div>
        </div>

        {/* Tasks for selected date */}
        <div>
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Tasks for {format(selectedDate, 'MMM d, yyyy')}
            </h3>
            
            {tasksForDate.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <div className="text-4xl mb-2">📅</div>
                <p>No tasks for this date</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tasksForDate.map(task => (
                  <TaskItem key={task.id} task={task} />
                ))}
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="card mt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Legend
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-gray-600 dark:text-gray-400">Has pending tasks</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-gray-600 dark:text-gray-400">Has high priority tasks</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-gray-600 dark:text-gray-400">All tasks completed</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CalendarPage